import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@material-tailwind/react";
import { Tooltip } from "@material-tailwind/react";
import { Edit, Info, History, UserPlus, X } from "lucide-react";
import UserEditModal from "../../pages/Customers/UserEditModal";
import axiosInstance from "../../services/api/axios";
import Select from "react-select";
import CostSheetHistoryModal from "../../components/CostSheetHistoryModal";
import InlineCreateBuyerModal from "./InlineCreateBuyerModal";

const PropertyBuyerDetails = ({
  formData,
  handleInputChange,
  property,
  customers,
  onCustomerSelect,
  userInfo,
  fullProperty, // Add this prop for the full property object
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isCreateBuyerModalOpen, setIsCreateBuyerModalOpen] = useState(false);
  const [isCreateCoApplicantModalOpen, setIsCreateCoApplicantModalOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [presalePersons, setPresalePersons] = useState([]);
  // Eligible referrer buyers for this project (source === REFERRAL) — ResponseEligibleReferrerData[].
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Buyers created in-session via the inline "+ New Buyer" affordance. Held
  // locally so the picker shows them immediately without forcing the
  // parent (SaleDialog / CostSheetFormModal) to re-fetch /customers/user.
  // Merged into `filteredCustomers` below.
  const [localCreatedBuyers, setLocalCreatedBuyers] = useState([]);


  // Resolve from the server list AND in-session created buyers. A buyer
  // created via the inline "+ New Buyer" flow lives only in
  // `localCreatedBuyers` until a parent re-fetch, so searching `customers`
  // alone left `selectedCustomer` undefined right after creation — which
  // meant the `onCustomerSelect` effect below never fired, the buyer detail
  // panel stayed blank, and downstream consumers (e.g. cost-sheet approval)
  // got no `userId`. Searching the merged pool fixes all three.
  const selectedCustomer = useMemo(() => {
    const uid = parseInt(formData.customerId);
    const pool = [...(customers || []), ...localCreatedBuyers];
    return pool.find((customer) => customer?.user?.userId === uid);
  }, [customers, localCreatedBuyers, formData.customerId]);

  const selectedAgent = useMemo(() => {
    return agents.find(
      (agent) => agent.customerId === parseInt(formData.agentId)
    );
  }, [agents, formData.agentId]);

  // eslint-disable-next-line no-unused-vars
  const selectedPresalePerson = useMemo(() => {
    return presalePersons.find(
      (person) => person.loginId === parseInt(formData.presalesId)
    );
  }, [presalePersons, formData.presalesId]);

  // Filter customers to show only those with isAccountCreated: true, then
  // append any buyers created via the inline "+ New Buyer" flow so they
  // appear immediately without a parent re-fetch.
  const filteredCustomers = useMemo(() => {
    const base = customers?.filter(customer => customer.user.isAccountCreated === true) || [];
    if (!localCreatedBuyers.length) return base;
    const existingIds = new Set(base.map((c) => c.user?.userId));
    const extras = localCreatedBuyers.filter((c) => !existingIds.has(c.user?.userId));
    return [...base, ...extras];
  }, [customers, localCreatedBuyers]);

  // Wraps a freshly-created /users response into the same shape that
  // /customers/user returns, so the picker's existing `customer.user.*`
  // accesses keep working without branching.
  const wrapNewUserAsCustomerEntry = (user) => ({
    user: {
      userId: user?.userId,
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      emailId: user?.emailId || "",
      mobileNoWa: user?.mobileNoWa || "",
      mobileNoCalling: user?.mobileNoCalling || "",
      isAccountCreated: true,
    },
    agent: null,
  });

  const handleBuyerCreated = (newUser) => {
    if (!newUser?.userId) return;
    const entry = wrapNewUserAsCustomerEntry(newUser);
    setLocalCreatedBuyers((prev) => [...prev, entry]);
    // Auto-select the newly created buyer in the picker.
    handleInputChange({ target: { name: "customerId", value: newUser.userId } });
  };

  // ─── Secondary buyers (co-applicants) ───────────────────────────────────
  // Co-applicants are FULL accounts, exactly like the primary buyer. They are
  // stored in formData.secondaryUserIds as an array of userId numbers and
  // updated through the same generic handleInputChange the primary buyer uses.
  const updateSecondaryUserIds = (next) => {
    handleInputChange({ target: { name: "secondaryUserIds", value: next } });
  };

  // Existing-buyer options for the add-control: reuse the primary buyer pool
  // but EXCLUDE the primary buyer and anyone already added as a co-applicant.
  const coApplicantOptions = useMemo(() => {
    const primaryId = parseInt(formData.customerId);
    const taken = new Set((formData.secondaryUserIds || []).map(Number));
    return filteredCustomers
      .filter((c) => {
        const uid = c.user?.userId;
        return uid && uid !== primaryId && !taken.has(uid);
      })
      .map((c) => ({
        value: c.user.userId,
        label: `${c.user.firstName} ${c.user.lastName} - ${c.user.emailId}`,
      }));
  }, [filteredCustomers, formData.customerId, formData.secondaryUserIds]);

  // Resolve currently-added co-applicant ids to display rows (name + id).
  const selectedCoApplicants = useMemo(() => {
    const pool = [...(customers || []), ...localCreatedBuyers];
    return (formData.secondaryUserIds || []).map((id) => {
      const num = Number(id);
      const found = pool.find((c) => c.user?.userId === num);
      return {
        userId: num,
        name: found
          ? `${found.user.firstName} ${found.user.lastName}`
          : `User #${num}`,
      };
    });
  }, [customers, localCreatedBuyers, formData.secondaryUserIds]);

  const handleAddCoApplicant = (selectedOption) => {
    if (!selectedOption) return;
    const uid = Number(selectedOption.value);
    // Guard against the primary buyer and duplicates.
    if (uid === parseInt(formData.customerId)) return;
    const current = (formData.secondaryUserIds || []).map(Number);
    if (current.includes(uid)) return;
    updateSecondaryUserIds([...current, uid]);
  };

  const handleRemoveCoApplicant = (uid) => {
    const next = (formData.secondaryUserIds || [])
      .map(Number)
      .filter((id) => id !== Number(uid));
    updateSecondaryUserIds(next);
  };

  // Inline-created co-applicant: surface it everywhere (localCreatedBuyers)
  // AND add it to the co-applicant list.
  const handleCoApplicantCreated = (newUser) => {
    if (!newUser?.userId) return;
    const entry = wrapNewUserAsCustomerEntry(newUser);
    setLocalCreatedBuyers((prev) => [...prev, entry]);
    const uid = Number(newUser.userId);
    if (uid === parseInt(formData.customerId)) return;
    const current = (formData.secondaryUserIds || []).map(Number);
    if (!current.includes(uid)) updateSecondaryUserIds([...current, uid]);
  };



  const openEditModal = () => setIsEditModalOpen(true);
  const closeEditModal = () => setIsEditModalOpen(false);
  
  // History modal handlers
  const handleOpenHistoryModal = () => setIsHistoryModalOpen(true);
  const handleCloseHistoryModal = () => setIsHistoryModalOpen(false);
  useEffect(() => {
    const projectId = property?.projectPhase?.project?.projectId;
    
    if (!projectId) return;
    
    // Fetch all agents from the API
    const fetchAllAgents = async () => {
      try {
        const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
        if (!customerId) {
          setLoading(false);
          return;
        }
        
        const response = await axiosInstance.get(
          `/customers/agent/project/configs?customerId=${customerId}`
        );
        
        const agentItems = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
            ? response.items
            : [];

        if (agentItems.length > 0) {
          const allAgents = agentItems.map((item) => ({
            ...item.agent,
            propertyConfigs: item.propertyConfigs,
            projects: item.projects,
          }));

          // Use all agents (not only those linked to current project)
          let mergedAgents = allAgents;
          if (selectedCustomer?.agent?.customerId) {
            const exists = mergedAgents.some(
              (a) => parseInt(a.customerId) === parseInt(selectedCustomer.agent.customerId)
            );
            if (!exists) {
              mergedAgents = [
                ...mergedAgents,
                {
                  ...selectedCustomer.agent,
                },
              ];
            }
          }

          // De-duplicate by customerId just in case
          const uniqueByCustomerId = [];
          const seen = new Set();
          for (const a of mergedAgents) {
            const id = parseInt(a.customerId);
            if (!seen.has(id)) {
              seen.add(id);
              uniqueByCustomerId.push(a);
            }
          }

          setAgents(uniqueByCustomerId);
        } else {
          setAgents([]);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching agents:", error);
        setAgents([]);
        setLoading(false);
      }
    };

    const fetchPresalePersons = async () => {
      try {
        // Replacement for the retired /project/sales/login endpoint:
        // RBAC role assignments give us (loginId, projectId) tuples; we then
        // look up each login to filter by STAFF persona.
        const customerId =
          userInfo?.loginIdentifierDetails?.customer?.customerId ||
          userInfo?.loginIdentifierDetails?.customerId;

        const [assignmentsResponse, loginsResponse] = await Promise.all([
          axiosInstance.get(`/role-assignments`, { params: { projectId } }),
          customerId
            ? axiosInstance.get(`/login`, {
                params: { loginIdentifier: customerId, personaType: "STAFF" },
              })
            : Promise.resolve([]),
        ]);

        const assignments = Array.isArray(assignmentsResponse) ? assignmentsResponse : [];
        const logins = Array.isArray(loginsResponse) ? loginsResponse : (loginsResponse?.items || []);
        const assignedLoginIds = new Set(assignments.map((a) => a.loginId));

        const presalePersonsList = logins
          .filter((l) => assignedLoginIds.has(l.loginId) && l.persona === "STAFF")
          .map((l) => ({
            loginId: l.loginId,
            firstName: (l.name || "").split(" ")[0] || "",
            lastName: (l.name || "").split(" ").slice(1).join(" ") || "",
            fullName: l.name || "",
            emailId: l.email,
          }));

        setPresalePersons(presalePersonsList);
      } catch (error) {
        console.error("Error fetching presale persons:", error);
        setPresalePersons([]);
      }
    };

    // Eligible referrer buyers (project owners) for the REFERRAL source picker.
    const fetchReferrers = async () => {
      try {
        const response = await axiosInstance.get(
          `/referrals/project/${projectId}/eligible-referrers`
        );
        setReferrers(Array.isArray(response) ? response : response?.items || []);
      } catch (error) {
        console.error("Error fetching eligible referrers:", error);
        setReferrers([]);
      }
    };

    // Fetch all agents from API
    fetchAllAgents();
    fetchPresalePersons();
    fetchReferrers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.projectPhase?.project?.projectId, userInfo]);
  useEffect(() => {
    if (formData.agentId) {
      const agent = agents.find(
        (agent) =>
          agent.customerId === parseInt(formData.agentId)
      );
      if (agent) {
        console.log("Selected Agent ID:", agent.customerId);
      }
    }
  }, [formData.agentId, agents]);

  // Notify parent when customer is selected
  useEffect(() => {
    if (selectedCustomer && onCustomerSelect) {
      onCustomerSelect(selectedCustomer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer]); // Remove onCustomerSelect from dependencies to prevent infinite loop

  // Auto-set source and agent when customer with agent is selected
  useEffect(() => {
   
    console.log("selectedCustomer.agent:", selectedCustomer?.agent);
    
 
    
    if (selectedCustomer && selectedCustomer.agent && !loading && agents.length > 0) {
    
      console.log("Agent details:", selectedCustomer.agent);
      
      // Auto-set source to CHANNEL_PARTNER
      const sourceEvent = {
        target: {
          name: "source",
          value: "CHANNEL_PARTNER"
        }
      };
    
      handleInputChange(sourceEvent);

      // Auto-set agent
      const agentEvent = {
        target: {
          name: "agentId",
          value: selectedCustomer.agent.customerId.toString()
        }
      };
 
      handleInputChange(agentEvent);
    } else {
      console.log("No agent found for selected customer or agents not loaded yet");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, loading, agents]);

  // When source is REFERRAL, default the "Referred by" picker to the referrer who already
  // tagged this buyer in the project (if any), mirroring how the CP is auto-selected.
  useEffect(() => {
    if (formData.source !== "REFERRAL" || !formData.customerId || formData.referrerUserId) return;
    const buyerId = parseInt(formData.customerId);
    const match = referrers.find((r) => (r.referredUserIds || []).map(Number).includes(buyerId));
    if (match) {
      handleInputChange({ target: { name: "referrerUserId", value: match.userId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.source, formData.customerId, referrers]);

  // Handle react-select onChange events
  const handleSelectChange = (selectedOption, fieldName) => {

    const syntheticEvent = {
      target: {
        name: fieldName,
        value: selectedOption ? selectedOption.value : ""
      }
    };
   
    handleInputChange(syntheticEvent);
  };

  return (
    <div className="space-y-6 overflow-y-auto max-h-[calc(90vh-200px)] shadow-none" >
      {/* Property Details Card */}
      {property ? (
        <>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-800">
              Property and Buyer Details
            </h2>
            <button
              onClick={handleOpenHistoryModal}
              className="flex items-center gap-2 px-2 py-2 text-sm rounded-md border border-gray-200 bg-white text-flamingo-500 font-medium hover:bg-flamingo-50 hover:border-flamingo-300 transition-colors shadow-sm"
              title="View Cost Sheet History"
            >
              <History size={16} strokeWidth={2} />
              <span>View History</span>
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {/* Property Details */}
            {[
              { label: "Flat No", value: property.propertyNo },
              { label: "Building No", value: property.buildingNo },
              { label: "Project Name", value: property.projectPhase.project.projectName },
              { label: "RERA No.", value: property.projectPhase.reraNo },
              { label: "Status", value: property.status },
              { label: "Property Type", value: property.propertyType },
            ].map((detail, index) => (
              <div key={index}>
                <p className="text-sm text-slate-600">{detail.label}</p>
                <p className="font-normal">{detail.value}</p>
              </div>
            ))}
            {/* Agent Details within Property Card */}
            {selectedAgent && (
              <div className="md:col-span-2">
                <p className="font-medium">
                  {selectedAgent.ownerName}
                </p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <p className="text-slate-500">
            No property selected. Please select a property from the inventory.
          </p>
        </div>
      )}

      {/* Customer, Source and Channel Partner Selection */}
      <div className="">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Customer Selection */}
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Select Customer <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setIsCreateBuyerModalOpen(true)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-flamingo-600 hover:text-flamingo-700"
                >
                  <UserPlus size={13} />
                  New buyer
                </button>
              </div>
              <Select
                value={filteredCustomers.find(customer => customer.user.userId === parseInt(formData.customerId)) ?
                  { value: formData.customerId, label: `${filteredCustomers.find(c => c.user.userId === parseInt(formData.customerId))?.user.firstName} ${filteredCustomers.find(c => c.user.userId === parseInt(formData.customerId))?.user.lastName} - ${filteredCustomers.find(c => c.user.userId === parseInt(formData.customerId))?.user.emailId}` } :
                  null
                }
                onChange={(selectedOption) => handleSelectChange(selectedOption, "customerId")}
                options={filteredCustomers.map((customer) => ({
                  value: customer.user.userId,
                  label: `${customer.user.firstName} ${customer.user.lastName} - ${customer.user.emailId}`,
                }))}
                placeholder="Select Customer"
                isClearable
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                maxMenuHeight={280}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, maxHeight: 280, overflowY: 'auto' }),
                }}
              />
              {filteredCustomers.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  No customers yet — use <span className="font-medium text-flamingo-600">+ New buyer</span> to create one.
                </p>
              )}
            </div>

            <Tooltip placement="right">
              <Tooltip.Trigger asChild>
                <Info className="h-4 w-4 text-gray-500 cursor-help mt-6" />
              </Tooltip.Trigger>
              <Tooltip.Content className="bg-slate-800 text-white font-normal text-sm">
                Only customers with created accounts will appear in this list
                <Tooltip.Arrow />
              </Tooltip.Content>
            </Tooltip>
          </div>

          {/* Source Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Source <span className="text-red-500">*</span>
            </label>
            <Select
              value={formData.source ? { value: formData.source, label: formData.source } : null}
              onChange={(selectedOption) => handleSelectChange(selectedOption, "source")}
              options={[
                { value: "CHANNEL_PARTNER", label: "CHANNEL_PARTNER" },
                { value: "PRE_SALES", label: "PRE_SALES" },
                { value: "REFERRAL", label: "REFERRAL" },
                { value: "HOARDING", label: "HOARDING" },
                { value: "META", label: "META" },
                { value: "GOOGLE", label: "GOOGLE" },
                { value: "YOUTUBE", label: "YOUTUBE" },
                { value: "PROPERTY_PORTALS", label: "PROPERTY_PORTALS" },
              ]}
              placeholder="Select Source"
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              maxMenuHeight={280}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          {/* Agent Selection - Only show when CHANNEL_PARTNER is selected */}
          {formData.source === "CHANNEL_PARTNER" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Channel Partner 
              </label>
              <Select
                value={agents.find(agent => agent.customerId === parseInt(formData.agentId)) ? 
                  { value: formData.agentId, label: agents.find(a => a.customerId === parseInt(formData.agentId))?.ownerName } : 
                  null
                }
                onChange={(selectedOption) => handleSelectChange(selectedOption, "agentId")}
                options={agents.map((agent) => ({
                  value: agent.customerId,
                  label: agent.ownerName,
                }))}
                placeholder="Select Channel Partner"
                isClearable
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                maxMenuHeight={280}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          ) : formData.source === "PRE_SALES" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Presale Person 
              </label>
              <Select
                value={presalePersons.find(person => person.loginId === parseInt(formData.presalesId)) ? 
                  { value: formData.presalesId, label: presalePersons.find(p => p.loginId === parseInt(formData.presalesId))?.fullName } : 
                  null
                }
                onChange={(selectedOption) => handleSelectChange(selectedOption, "presalesId")}
                options={presalePersons.map((person) => ({
                  value: person.loginId,
                  label: `${person.fullName} - ${person.emailId}`,
                }))}
                placeholder="Select Presale Person"
                isClearable
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                maxMenuHeight={280}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          ) : formData.source === "REFERRAL" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Referred By
              </label>
              <Select
                value={
                  referrers.find((r) => r.userId === parseInt(formData.referrerUserId))
                    ? {
                        value: formData.referrerUserId,
                        label:
                          referrers.find((r) => r.userId === parseInt(formData.referrerUserId))?.name ||
                          `User #${formData.referrerUserId}`,
                      }
                    : null
                }
                onChange={(selectedOption) => handleSelectChange(selectedOption, "referrerUserId")}
                options={referrers.map((r) => ({
                  value: r.userId,
                  label: `${r.name || `User #${r.userId}`}${r.mobileNoWa ? ` - ${r.mobileNoWa}` : ""}`,
                }))}
                placeholder="Select Referrer"
                isClearable
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                maxMenuHeight={280}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          ) : (
            <div></div>
          )}
        </div>

        {/* Referral reward amount — captured at sale time, gates reward creation */}
        {formData.source === "REFERRAL" && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-start-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Referral Reward Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                name="referralRewardAmount"
                value={formData.referralRewardAmount || ""}
                onChange={handleInputChange}
                placeholder="Reward amount"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-flamingo-500 focus:outline-none focus:ring-1 focus:ring-flamingo-500"
              />
            </div>
          </div>
        )}

        {/* Customer Details */}
        {selectedCustomer ? (
          <div className="mt-4 border-t mb-6 pt-4  max-h-[400px]">
            <div className="flex justify-between items-center mb-4">
              <h3 className=" font-medium text-slate-800">Customer Details</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={openEditModal}
                color="black"
                className="px-2 py-1"
              >
                <Edit size={20} />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Details */}
              <div>
                <p className="text-sm text-slate-600">Name</p>
                <p className="font-normal">
                  {selectedCustomer.user.firstName} {selectedCustomer.user.lastName}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Email</p>
                <p className="font-normal">{selectedCustomer.user.emailId}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Phone (Calling)</p>
                <p className="font-normal">
                  {selectedCustomer.user.mobileNoCalling}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Phone (WhatsApp)</p>
                <p className="font-normal">{selectedCustomer.user.mobileNoWa}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Gender</p>
                <p className="font-normal">{selectedCustomer.user.gender}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">DOB</p>
                <p className="font-normal">{selectedCustomer.user.dob}</p>
              </div>

              {/* Address Details */}
              <div className="md:col-span-2">
                <p className="text-sm text-slate-600">Address</p>
                <p className="font-normal">
                    {selectedCustomer?.user?.address
                    ? `${selectedCustomer.user.address.buildingNo || ''}, ${selectedCustomer.user.address.streetName1 || ''}, ${selectedCustomer.user.address.streetName2 || ''}, ${selectedCustomer.user.address.city || ''}, ${selectedCustomer.user.address.state || ''}, ${selectedCustomer.user.address.country || ''}, ${selectedCustomer.user.address.pinCode || ''}`
                    : "No address provided"}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 border-t mb-6 pt-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-slate-800">Customer Details</h3>
            </div>
            <div className="text-center py-8 bg-slate-50 rounded-lg">
              <p className="text-slate-500">
                Please select a customer to view their details
              </p>
            </div>
          </div>
        )}

        {/* Secondary Buyers (Co-applicants) — optional. Full accounts, added by
            selecting an existing buyer or creating a new one inline, exactly
            like the primary buyer. Stored in formData.secondaryUserIds. */}
        <div className="mt-4 border-t pt-4">
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Secondary Buyers (Co-applicants)
            </label>
            <button
              type="button"
              onClick={() => setIsCreateCoApplicantModalOpen(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-flamingo-600 hover:text-flamingo-700"
            >
              <UserPlus size={13} />
              New co-applicant
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-2">
            Optional. Add one or more co-applicants who will share ownership on this sale.
          </p>

          {selectedCoApplicants.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedCoApplicants.map((ca) => (
                <span
                  key={ca.userId}
                  className="inline-flex items-center gap-1.5 rounded-full bg-flamingo-50 border border-flamingo-200 px-3 py-1 text-sm text-flamingo-700"
                >
                  {ca.name}
                  <button
                    type="button"
                    onClick={() => handleRemoveCoApplicant(ca.userId)}
                    className="text-flamingo-500 hover:text-flamingo-700"
                    aria-label={`Remove ${ca.name}`}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <Select
            value={null}
            onChange={handleAddCoApplicant}
            options={coApplicantOptions}
            placeholder="Add existing buyer as co-applicant"
            isClearable={false}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            maxMenuHeight={280}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, maxHeight: 280, overflowY: 'auto' }),
            }}
          />
        </div>
      </div>

      {/* Edit User Modal */}
      {isEditModalOpen && (
        <UserEditModal
          user={selectedCustomer}
          isOpen={setIsEditModalOpen}
          onClose={closeEditModal}
        />
      )}

      {/* Cost Sheet History Modal */}
      <CostSheetHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        property={fullProperty}
        userInfo={userInfo}
        isNestedModal={true}
      />

      {/* Inline buyer creation — available on both Unified and Plus tiers.
          Hits POST /users?isAccountCreation=true which atomically creates
          user + login + customer_user relation. Plus tenants need this
          path because the bulk Leads-tab add/upload is plan-gated. */}
      <InlineCreateBuyerModal
        open={isCreateBuyerModalOpen}
        onClose={() => setIsCreateBuyerModalOpen(false)}
        developerCustomerId={userInfo?.loginIdentifierDetails?.customer?.customerId}
        projectId={property?.projectPhase?.project?.projectId}
        onCreated={handleBuyerCreated}
      />

      {/* Inline co-applicant creation — same full-account create flow as the
          primary buyer; on success the new user is added as a co-applicant. */}
      <InlineCreateBuyerModal
        open={isCreateCoApplicantModalOpen}
        onClose={() => setIsCreateCoApplicantModalOpen(false)}
        developerCustomerId={userInfo?.loginIdentifierDetails?.customer?.customerId}
        projectId={property?.projectPhase?.project?.projectId}
        onCreated={handleCoApplicantCreated}
      />
    </div>
  );
};

export default PropertyBuyerDetails;
